import * as server_api from '../../../common/src/server_api.ts';
import { setFetchOverride } from '../../../common/src/server_api.ts';
import {
  addListener,
  AppearanceSetting,
  coerceIOSAppVersionToString,
  dropMalformedCachedSingingPlans,
  isAppearancePDFSpecific,
  localStorageGet,
  LocalStorageKey,
  localStorageRemove,
  registerForLocalStorageEvents,
  reverifyOrgHymnalDownloadOnce,
} from '../data/client_local_storage.ts';
import { initializeCurrentSongUpdateTimestamp } from '../data/song_updates.ts';
import { synchronizeOrgsWithServer, synchronizeWeeklySongLists } from '../data/use_current_org.tsx';
import { synchronizeHouseholdWithServer } from '../data/use_household.tsx';
import { generateMidiNoteNames, loadSoundfont } from '../player/instrument.ts';
import { isInsideAppleAppStoreApp, requestIOSAppVersion } from './billing.ts';
import { reportCrash } from './crash.ts';
import { forwardConsoleLogToIOS } from './ios_logging.ts';
import { handleLiveUpdateMessage } from './live_update_messages.ts';
import { scheduleLiveUpdateRefresh } from './live_update_refresh.ts';
import { LiveUpdateConnectInfo, noteAppFetchSucceeded, startAppLiveUpdates } from './live_updates.ts';
import { recordConsoleLog } from './logging.ts';
import { registerMusicCacheRefreshTriggers } from './music_cache_refresh.ts';
import { initializePDFWorker } from './pdf_viewer.tsx';
import { addPromiseWithResolversPolyfill } from './promise_with_resolvers_polyfill.ts';
import { registerPushNotificationHandlers } from './push_notifications.ts';
import { cssColorToHex } from './shared.tsx';
import { monkeyPatchPushState } from './use_location.tsx';
import { recordBundleHash } from './use_music_cache.tsx';
import { registerUserActivityListeners } from './user_activity.ts';

export function initializeApp(navigate: (to: string) => Promise<void>) {
  applyTheme();
  listenForThemeChanges();
  recordConsoleLog();
  addPromiseWithResolversPolyfill();
  setFetchOverride(async (url, init) => {
    const response = await fetch(url, init);
    // a successful request is often the first hard evidence a mobile browser gives that the network
    // is back; this is a no-op while the live-update socket is connected, so it costs nothing here
    if (response.ok) {
      noteAppFetchSucceeded();
    }
    return response;
  });
  // the org hymnal download indicator is no longer a reentrancy guard (issue #3517) - drop any
  // value left behind by a reload (or an older bundle) so the Library does not look stuck
  localStorageRemove(LocalStorageKey.OrgHymnalDownloadHashingHash);

  startAppLiveUpdates({ onConnect: catchUpOnLiveUpdates, onMessage: handleLiveUpdateMessage });

  initializePDFWorker();
  void initializeCurrentSongUpdateTimestamp();

  recordBundleHash();
  registerErrorHandler();
  // the update gate holds a page reload while someone is typing or clicking (issue #3265)
  registerUserActivityListeners();

  registerForLocalStorageEvents();
  monkeyPatchPushState();
  void loadSoundfont();
  generateMidiNoteNames();
  server_api.registerUserTokenCallbacks({
    userCallback: () => localStorageGet(LocalStorageKey.ActiveUserToken),
    householdCallback: () => localStorageGet(LocalStorageKey.Household)?.token,
  });

  initializeForMobile();
  registerMusicCacheRefreshTriggers();
  void synchronizeWithServer();
  registerPushNotificationHandlers(navigate);

  // TODO(remove after 2026-12-01): deprecated with the getAppVersion reply; delete with
  // coerceIOSAppVersionToString and LocalStorageKey.IOSAppVersion
  coerceIOSAppVersionToString();
  dropMalformedCachedSingingPlans();
  // TODO(remove after 2027-04-06): clears a wrongly recorded org hymnal hash once per device; delete
  // with reverifyOrgHymnalDownloadOnce
  reverifyOrgHymnalDownloadOnce();
}

// Catch up on whatever the live-update socket may have missed while it was closed: the weekly song
// lists, and the music and bundle hashes. Only reconnects count: the first open lands while
// initializeApp's own synchronizeWithServer() and music-cache checks are still running, and nothing
// can have been missed before a socket ever existed. Every server restart (so every deploy)
// produces a reconnect, which makes this the main path by which a new bundle reaches a production
// client; after an ordinary outage the walk simply finds nothing changed and does nothing.
function catchUpOnLiveUpdates({ isReconnect }: LiveUpdateConnectInfo) {
  if (!isReconnect) {
    return;
  }
  // both behind the same jitter: a reconnect herd arrives together after a deploy
  scheduleLiveUpdateRefresh({ syncWeeklySongLists: true });
}

export function applyTheme() {
  // documentElement can be null when the document is being torn down,
  // e.g. an embedded song island iframe removed by the host page mid-render (crash report #75121)
  const root: HTMLElement | null = document.documentElement;
  if (!root) {
    return;
  }
  const darkMode = localStorageGet(LocalStorageKey.Appearance);
  if (!isAppearancePDFSpecific && darkMode === AppearanceSetting.Light) {
    root.dataset.theme = 'light';
  } else if (!isAppearancePDFSpecific && darkMode === AppearanceSetting.Dark) {
    root.dataset.theme = 'dark';
  } else {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.dataset.theme = prefersDark ? 'dark' : 'light';
  }

  if (isAppearancePDFSpecific && darkMode === AppearanceSetting.Light) {
    root.dataset.themePdf = 'light';
  } else if (isAppearancePDFSpecific && darkMode === AppearanceSetting.Dark) {
    root.dataset.themePdf = 'dark';
  } else {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.dataset.themePdf = prefersDark ? 'dark' : 'light';
  }

  const isPlayerMaximized = localStorageGet(LocalStorageKey.IsPlayerMaximized);
  const usePdfTheme = isAppearancePDFSpecific && isPlayerMaximized;
  const backgroundProperty = usePdfTheme ? '--color-pdf-background' : '--color-background';
  const cssBackgroundColor = getComputedStyle(root).getPropertyValue(backgroundProperty);
  const backgroundColor = cssColorToHex(cssBackgroundColor);
  const themeForFallback = usePdfTheme ? root.dataset.themePdf : root.dataset.theme;
  const bandColor = backgroundColor ?? (themeForFallback === 'dark' ? '#000000' : '#1e1e1e');
  // The store apps paint this colour behind the system bars; the first one also ends their native splash
  window.webkit?.messageHandlers?.setBandColor?.postMessage({ color: bandColor });
  window.SypNative?.setBandColor?.(bandColor);
}

function listenForThemeChanges() {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    applyTheme();
  });
  addListener(LocalStorageKey.Appearance, () => {
    applyTheme();
  });
  addListener(LocalStorageKey.IsPlayerMaximized, () => {
    applyTheme();
  });
}

// notify native app if a crash occurs
// the native app now ignores this because many crashes are benign, so why interrupt the app
function registerErrorHandler() {
  window.addEventListener('error', function (event: ErrorEvent) {
    if (event.error.hasBeenCaught !== undefined) {
      return false;
    }
    event.error.hasBeenCaught = true;
    const message = {
      message: event.message,
      url: event.filename,
      line: event.lineno,
      column: event.colno,
      error: JSON.stringify(event.error),
    };

    if (window.webkit) {
      window.webkit.messageHandlers.error.postMessage(message);
    } else {
      console.error(JSON.stringify(message));
    }
  });

  window.addEventListener('error', reportCrash);
}

function initializeForMobile() {
  if (isInsideAppleAppStoreApp()) {
    requestIOSAppVersion();
    forwardConsoleLogToIOS();
  }
}

async function synchronizeWithServer() {
  // properly order initialization
  // each clause can independently fail without preventing subsequent clauses from running
  await synchronizeHouseholdWithServer({ force: true });
  await synchronizeOrgsWithServer({ force: true });
  await synchronizeWeeklySongLists();
}
